// apps/web/src/pages/author/RecipeEditor.tsx
//
// Phase 8 Plan 10 Task 2 — Top-level Author UI page (authr-01). // sanitize-allow: [A-Z]{3,5}-[0-9]{2}
//
// COMPONENT TREE (UI-SPEC author-UI shell component tree):
//
//   <div className="h-screen flex flex-col">
//     <FormProvider {...form}>
//       <EditorShell toolbar={<EditorToolbarWrapper .../>}>
//         <SplitPane storageKey="..." ...>
//           leftChildren=<RecipeFormPanel />
//           rightChildren=<PreviewPane />
//         </SplitPane>
//       </EditorShell>
//       <PasteJsonDialog open=... onClose=.../>
//     </FormProvider>
//   </div>
//
// EditorShell renders the page-level <main aria-label="Recipe editor"> AND
// the UnsavedChangesGuard at its leaf — see EditorShell.tsx.
//
// W5 — 401 fallback imports `computeAllowedReturnTo` from `@/lib/safe-return-to`
//      (Plan 08-11 single source of truth). NEVER inline the regex here; SignIn
//      and RecipeEditor are the two clients of the helper.
//
// W7 — "Mid-complexity" fixture menu item maps to the
//      02-cocktail.recipe.json fixture (per STRESSES.md — middle of the gradient). // grep-allow: cocktail
//
// W15 — recipeName subscription uses `useWatch({ control, name: 'title' })`
//      inside an inline EditorToolbarWrapper component. NEVER the RHF watch
//      method on the form instance — that path skips renders under React 19
//      Compiler (CONTEXT D-07).
//
// SANITIZATION (UI-SPEC §"Sanitization" lines 867-868):
//   - Paste-JSON path: JSON.parse in try/catch → RecipeDefinitionV1.safeParse
//     → form.reset (with destructive-confirm Dialog when form is dirty).
//   - Fixture-load path: defense-in-depth safeParse on every fixture even
//     though the fixtures live in-repo (META-07). // sanitize-allow: [A-Z]{3,5}-[0-9]{2}
//
// SERVER-ERROR FALLBACKS (UI-SPEC §"Server-error fallbacks" lines 383-393):
//   network → toast.error (sonner)
//   422     → form.setError(field, {type:'server', message}) per issue
//   403     → persistent Alert + disabled Save
//   401     → navigate /auth/sign-in?returnTo=<computeAllowedReturnTo>
//   404     → persistent Alert "Recipe not found"
//   5xx     → toast.error + Try-again button
//
// grep-allow markers below are required because the file references the
// fixture filenames by their canonical recipe-domain names. Each line that
// carries a forbidden token has its own per-pattern marker.
//
// grep-allow: pancake, cocktail

import { useMutation, useQuery } from "@tanstack/react-query";
import {
  type ExtractionMeta,
  type RecipeDefinitionV1,
  RecipeDefinitionV1 as RecipeDefinitionV1Schema,
} from "@the-recipe-spread/shared";
import oneLineFixture from "@the-recipe-spread/shared/fixtures/00-one-line.recipe.json" with {
  type: "json",
};
// W7: Mid-complexity fixture menu item maps to a mid-gradient fixture per
// STRESSES.md (middle of the complexity gradient).
// biome-ignore format: keep on one line so the trailing grep-allow marker stays same-line as the fixture token (META-07 gate is same-line).
import cocktailFixture from "@the-recipe-spread/shared/fixtures/02-cocktail.recipe.json" with { type: "json" }; // grep-allow: cocktail
// biome-ignore format: keep on one line so the trailing grep-allow marker stays same-line as the fixture token (META-07 gate is same-line).
import pancakeFixture from "@the-recipe-spread/shared/fixtures/05-pancake.recipe.json" with { type: "json" }; // grep-allow: pancake
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  type FieldValues,
  FormProvider,
  type Resolver,
  type UseFormReturn,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { SplitPane } from "@/components/layout/SplitPane";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
// Plan 09-13 Rule 1 auto-fix: ConflictBadge (constraint-config) uses Radix
// Tooltip components which require a <Tooltip.Provider> ancestor (Phase 9
// shadcn registry add). Production RecipeEditor was missing the provider; the
// AUTHR-06 + AUTHR-04 benches surfaced this when Constraints.tsx mounted the
// ConflictBadge in the rendered tree. Wrap with a default delay; Phase 9
// surfaces use delayDuration=300/skipDelay=500 in spec — production-default
// values are fine here (no tooltips active until a conflict surfaces).
import { TooltipProvider } from "@/components/ui/tooltip";
import { computeAllowedReturnTo } from "@/lib/safe-return-to";
// Plan 09-13 Rule 1 auto-fix: cardinality-config + other constraint sub-forms
// call useTerm() at component top-level (Plan 09-08/09-10 wiring). useTerm
// requires a SpreadVocabularyProvider ancestor; without one it throws and the
// AUTHR-06 + AUTHR-04 benches' RecipeEditor mount surfaces the error. The
// author UI is recipe-level (no spread context), so we wrap with empty
// overrides → defaults flow through. This preserves the useTerm contract
// (provider required) while accommodating author-time recipe editing.
import { SpreadVocabularyProvider } from "@/lib/vocabulary/SpreadVocabularyProvider";
import { AISuggestionProvider, type ExtractionDraftFlags } from "./AISuggestionContext";
import { EditorShell } from "./EditorShell";
import { EditorToolbar, type FixtureId } from "./EditorToolbar";
import { ExtractionDraftBanner } from "./ExtractionDraftBanner";
import { PreviewPane } from "./PreviewPane";
import { RecipeFormPanel } from "./RecipeFormPanel";

// ---------------------------------------------------------------------------
// Phase 10 Plan 12 (Surface 3) — the loaded recipe carries out-of-band // sanitize-allow: [A-Z]{3,5}-[0-9]{2}
// extraction sidecar fields that the RecipeDefinitionV1 BODY does not model
// (extraction_partial / is_ai_extracted_unreviewed are recipe-row columns per
// the Plan 10-02 schema; extraction_meta is the Plan 10-01 envelope, here
// extended with the Plan 10-08 GET-review aiSuggestedPaths list per UI-SPEC
// Open Q #8). RecipeEditor reads these to drive the draft banner + the
// AI-suggested annotation layer. Absence of every field degrades gracefully
// (no banner, no annotation).
//
// NOTE (Open Q #8 / Plan 10-08 handoff): `aiSuggestedPaths` is NOT yet a field
// on the shared ExtractionMeta schema. Plan 10-12 reads it via this local
// widening; Plan 10-08 should add `aiSuggestedPaths?: string[]` to the GET
// /api/recipes/:id/review response shape (and to extraction-meta.ts) so the
// annotation layer lights up end-to-end. Until then useAISuggestedPaths
// resolves to an empty set and the panel renders unannotated.
// ---------------------------------------------------------------------------
type ExtractionMetaWithPaths = ExtractionMeta & {
  aiSuggestedPaths?: readonly string[];
};

interface RecipeExtractionSidecar {
  extraction_partial?: boolean;
  is_ai_extracted_unreviewed?: boolean;
  extraction_meta?: ExtractionMetaWithPaths | null;
}

type RecipeWithExtractionMeta = RecipeDefinitionV1 & RecipeExtractionSidecar;

// ---------------------------------------------------------------------------
// Minimal-valid recipe shape for a brand-new recipe. Note: ingredient/
// instruction fields are required min(1) — so a brand-new recipe is NOT
// schema-valid until the user fills in title + a real ingredient/instruction.
// `mode: "onBlur"` keeps validation silent on first render; users see
// per-field FormMessages only after blur.
// ---------------------------------------------------------------------------
function makeEmptyRecipe(): RecipeDefinitionV1 {
  return {
    schemaVersion: 1,
    title: "",
    ingredientGroups: [{ label: "Main", items: [{ name: "" }] }],
    instructions: [{ text: "" }],
    widgets: [],
  };
}

// ---------------------------------------------------------------------------
// EditorToolbarWrapper (W15) — per-path useWatch subscription for the
// toolbar's `recipeName` prop. Lives inside RecipeEditor body so it has
// access to form context via useFormContext. Subscribes to ONLY 'title' —
// every other useWatch in this file follows the same per-path discipline.
// ---------------------------------------------------------------------------
interface EditorToolbarWrapperProps {
  onSave: () => void;
  onDiscard: () => void;
  onLoadFixture: (id: FixtureId) => void;
  onPasteJson: () => void;
  // WR-09 — `isSaving` plumbs the saveMutation.isPending signal through to
  // the toolbar so the Save button remains disabled across the entire
  // network round-trip, not just during handleSubmit's synchronous body.
  isSaving: boolean;
}

function EditorToolbarWrapper(props: EditorToolbarWrapperProps): React.ReactElement {
  const { control } = useFormContext();
  // W15: per-path useWatch ONLY. Do NOT subscribe via the RHF instance-watch
  // surface — that path skips renders under React 19 Compiler (CONTEXT D-07
  // + project conventions doc). The form is typed as FieldValues at the page
  // level (resolver workaround for the recipe schema input being unknown —
  // see useForm comment above); the 'title' string-path is type-safe at the
  // schema layer.
  const title = useWatch({ control, name: "title" }) as string | undefined;
  return (
    <EditorToolbar
      recipeName={title}
      onSave={props.onSave}
      onDiscard={props.onDiscard}
      onLoadFixture={props.onLoadFixture}
      onPasteJson={props.onPasteJson}
      isSaving={props.isSaving}
    />
  );
}

// ---------------------------------------------------------------------------
// PasteJsonDialog — focused state machine for the Paste-JSON flow.
// JSON.parse in try/catch → RecipeDefinitionV1.safeParse → form.reset.
// Failures surface inline in the Dialog (NOT a toast — the user is in a
// modal flow and needs the error context tied to the textarea).
// ---------------------------------------------------------------------------
interface PasteJsonDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onAccept: (parsed: RecipeDefinitionV1) => void;
}

function PasteJsonDialog({
  open,
  onOpenChange,
  onAccept,
}: PasteJsonDialogProps): React.ReactElement {
  const [text, setText] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setText("");
      setError(null);
    }
  }, [open]);

  function handleReplace(): void {
    setError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not parse JSON";
      setError(`Invalid JSON: ${msg}`);
      return;
    }
    const validation = RecipeDefinitionV1Schema.safeParse(parsed);
    if (!validation.success) {
      const firstIssue = validation.error.issues[0];
      const issueAt = firstIssue?.path?.join(".") ?? "";
      const issueMsg = firstIssue?.message ?? "Invalid recipe shape";
      setError(`This JSON is not a valid recipe definition (${issueAt}: ${issueMsg}).`);
      return;
    }
    onAccept(validation.data);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Paste recipe JSON</DialogTitle>
        </DialogHeader>
        <Textarea
          aria-label="Recipe JSON"
          rows={12}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="font-mono text-xs"
        />
        {error !== null ? (
          <Alert variant="destructive">
            <AlertTitle>Couldn't parse the JSON</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" autoFocus onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleReplace}>
            Replace recipe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// ReplaceConfirmDialog — destructive-confirm Dialog gating fixture load
// when the form is dirty (UI-SPEC §"Destructive confirmations" line 375).
// ---------------------------------------------------------------------------
interface ReplaceConfirmDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onConfirm: () => void;
}

function ReplaceConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
}: ReplaceConfirmDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Replace current recipe?</DialogTitle>
        </DialogHeader>
        <p>Your unsaved changes will be lost.</p>
        <DialogFooter>
          <Button type="button" variant="outline" autoFocus onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            Replace recipe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// RecipeEditor — top-level page.
//
// Tests pass an `initialRecipe` prop to seed the form deterministically;
// production resolves the recipe by URL param via TanStack Query.
// ---------------------------------------------------------------------------
export interface RecipeEditorProps {
  // Accepts the RecipeDefinitionV1 body OPTIONALLY carrying the Phase 10
  // extraction sidecar fields (extraction_partial / is_ai_extracted_unreviewed
  // / extraction_meta.aiSuggestedPaths). Tests seed these to exercise the
  // draft banner + annotation layer; production threads them from the loaded
  // recipe row.
  initialRecipe?: RecipeWithExtractionMeta | undefined;
}

const FIXTURE_BY_ID: Readonly<Record<FixtureId, unknown>> = Object.freeze({
  pancake: pancakeFixture, // grep-allow: pancake
  "one-line": oneLineFixture,
  cocktail: cocktailFixture, // grep-allow: cocktail
});

export function RecipeEditor(props: RecipeEditorProps): React.ReactElement {
  const params = useParams<{ recipeId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const recipeId = params.recipeId;

  // Server-fetch query. Disabled when we already have an initialRecipe
  // (tests) or when we're authoring a brand-new recipe.
  const enableQuery =
    props.initialRecipe === undefined && typeof recipeId === "string" && recipeId.length > 0;
  const recipeQuery = useQuery<RecipeWithExtractionMeta>({
    queryKey: ["recipe", recipeId],
    enabled: enableQuery,
    queryFn: async () => {
      const res = await fetch(`/api/recipes/${recipeId}`);
      if (!res.ok) {
        throw new Error(`Failed to load recipe: ${res.status}`);
      }
      const body = (await res.json()) as unknown;
      const parsed = RecipeDefinitionV1Schema.safeParse(body);
      if (!parsed.success) {
        throw new Error("Server returned a recipe that doesn't match the v1 schema");
      }
      // safeParse strips unknown keys, but the Phase 10 extraction sidecar
      // fields (extraction_partial / is_ai_extracted_unreviewed /
      // extraction_meta) live OUTSIDE the v1 body schema. Re-attach them from
      // the raw body so the draft banner + annotation layer can read them.
      // Under exactOptionalPropertyTypes, omit any sidecar key that is absent
      // rather than setting it to `undefined`.
      const sidecar = (body ?? {}) as RecipeExtractionSidecar;
      const attached: RecipeWithExtractionMeta = { ...parsed.data };
      if (sidecar.extraction_partial !== undefined) {
        attached.extraction_partial = sidecar.extraction_partial;
      }
      if (sidecar.is_ai_extracted_unreviewed !== undefined) {
        attached.is_ai_extracted_unreviewed = sidecar.is_ai_extracted_unreviewed;
      }
      if (sidecar.extraction_meta !== undefined) {
        attached.extraction_meta = sidecar.extraction_meta;
      }
      return attached;
    },
  });

  // sourceRecipe retains the extraction sidecar fields; initialDefaults is the
  // v1 body the form seeds from (the form never sees the sidecar fields).
  const sourceRecipe: RecipeWithExtractionMeta | undefined =
    props.initialRecipe ?? recipeQuery.data;
  const initialDefaults: RecipeDefinitionV1 = sourceRecipe ?? makeEmptyRecipe();

  // -------------------------------------------------------------------------
  // Phase 10 Plan 12 (Surface 3) — annotation-layer state. Both values are // sanitize-allow: [A-Z]{3,5}-[0-9]{2}
  // memoized over the loaded recipe.extraction_meta so the AISuggestionProvider
  // value reference is STABLE for the editing session (10-UI-SPEC §Performance
  // Contract lines 856-858 + T-10-12-01 render-storm defense). They recompute
  // only when the underlying extraction fields change (i.e. on load / on save).
  // -------------------------------------------------------------------------
  const extractionPartial = sourceRecipe?.extraction_partial ?? false;
  const isAiUnreviewed = sourceRecipe?.is_ai_extracted_unreviewed ?? false;
  const extractionMeta = sourceRecipe?.extraction_meta ?? null;

  const flags: ExtractionDraftFlags = useMemo(() => {
    const sourceUrlOrHash = extractionMeta?.sourceUrl ?? extractionMeta?.sourceContentHash;
    return {
      extraction_partial: extractionPartial,
      is_ai_extracted_unreviewed: isAiUnreviewed,
      ...(sourceUrlOrHash !== undefined ? { sourceUrlOrHash } : {}),
    };
  }, [extractionPartial, isAiUnreviewed, extractionMeta]);

  const suggestedPaths = useMemo<ReadonlySet<string>>(
    () => new Set(extractionMeta?.aiSuggestedPaths ?? []),
    [extractionMeta],
  );

  const aiSuggestionValue = useMemo(() => ({ flags, suggestedPaths }), [flags, suggestedPaths]);

  // Resolver wiring: RecipeDefinitionV1 is `z.ZodType<RecipeDefinitionV1, unknown>`
  // — its input type is `unknown`, which conflicts with zodResolver's
  // `FieldValues` (= Record<string, unknown>) constraint under Zod v4. The
  // existing `apps/web/src/pages/author/sections/IdentityFields.test.tsx`
  // header documents the same conflict for the section tests; here we apply
  // the same workaround at the page level: a thin custom resolver that wraps
  // the schema's `safeParse` and maps the Zod issue list into RHF's
  // FieldErrors shape. The form values are typed as `FieldValues` (NOT
  // `RecipeDefinitionV1`) so the resolver type lines up without an
  // `as unknown as` cast on the schema itself.
  const recipeResolver: Resolver<FieldValues> = async (values) => {
    const result = RecipeDefinitionV1Schema.safeParse(values);
    if (result.success) {
      return {
        values: result.data as unknown as FieldValues,
        errors: {},
      };
    }
    // Map Zod issues into the dot-path FieldErrors shape. RHF's Resolver
    // error branch requires `values: {}` (an empty record) — pass the empty
    // sentinel and let RHF skip submission on this path.
    type ErrorEntry = { type: string; message: string };
    const errors: Record<string, ErrorEntry> = {};
    for (const issue of result.error.issues) {
      const key = issue.path.join(".");
      if (errors[key] === undefined) {
        errors[key] = { type: "zod", message: issue.message };
      }
    }
    return { values: {}, errors };
  };

  const form = useForm<FieldValues>({
    resolver: recipeResolver,
    defaultValues: initialDefaults as unknown as FieldValues,
    mode: "onBlur",
  });

  // Re-seed defaults when a deferred recipeQuery lands (tests don't hit this
  // branch — only relevant when initialRecipe is undefined AND server query
  // completes). Use form.reset so isDirty rolls back to false on load.
  useEffect(() => {
    if (props.initialRecipe !== undefined) return;
    if (recipeQuery.data !== undefined) {
      form.reset(recipeQuery.data as unknown as FieldValues);
    }
  }, [recipeQuery.data, props.initialRecipe, form.reset]);

  const [serverError, setServerError] = useState<{
    code: 403 | 404;
    message: string;
  } | null>(null);
  const [pasteOpen, setPasteOpen] = useState<boolean>(false);
  const [pendingFixture, setPendingFixture] = useState<RecipeDefinitionV1 | null>(null);

  // -------------------------------------------------------------------------
  // Save mutation — Plan 11 ships the actual /api/recipes POST + PUT
  // endpoints. Plan 10 wires the mutation against fetch() so the integration
  // path is live the moment Plan 11 lands; tests mock fetch directly.
  // -------------------------------------------------------------------------
  const saveMutation = useMutation<RecipeDefinitionV1, Error, RecipeDefinitionV1>({
    mutationFn: async (recipe) => {
      const url =
        typeof recipeId === "string" && recipeId.length > 0
          ? `/api/recipes/${recipeId}`
          : "/api/recipes";
      const method = typeof recipeId === "string" && recipeId.length > 0 ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(recipe),
      });
      if (!res.ok) {
        const err: Error & { status?: number; details?: unknown } = new Error(
          `Save failed: ${res.status}`,
        );
        err.status = res.status;
        try {
          err.details = await res.json();
        } catch {
          // body wasn't JSON; leave details undefined.
        }
        throw err;
      }
      const body = (await res.json()) as unknown;
      const parsed = RecipeDefinitionV1Schema.safeParse(body);
      if (!parsed.success) {
        throw new Error("Server returned a recipe that doesn't match the v1 schema");
      }
      return parsed.data;
    },
    onSuccess: (saved) => {
      setServerError(null);
      // Reset the form so isDirty flips back to false; toolbar transitions
      // from "Save recipe" → "Saved".
      form.reset(saved as unknown as FieldValues);
      // For new recipes, the server response carries the new id — but the
      // Recipe-v1 schema is in-body-only (id is out-of-band per recipe-v1
      // D-09 sidecar). Plan 11 will provide a typed wrapper that carries
      // both. For Plan 10 we navigate only when the URL recipeId is
      // currently absent and the response contains an id-shaped field.
      const newIdCandidate = (saved as unknown as { id?: unknown }).id;
      if (
        (recipeId === undefined || recipeId.length === 0) &&
        typeof newIdCandidate === "string" &&
        newIdCandidate.length > 0
      ) {
        navigate(`/author/recipe/${newIdCandidate}`, { replace: true });
      }
    },
    onError: (err) => {
      const status: number | undefined = (err as Error & { status?: number }).status;
      // Network error (no status — fetch threw or the response wasn't HTTP)
      if (status === undefined) {
        toast.error("Couldn't save recipe — check your connection and try again.");
        return;
      }
      switch (status) {
        case 401: {
          // 401 — sign-in fallback. computeAllowedReturnTo bounds the
          // returnTo path to internal allow-listed prefixes (W5).
          const safeReturn = computeAllowedReturnTo(location.pathname);
          navigate(`/auth/sign-in?returnTo=${encodeURIComponent(safeReturn)}`);
          return;
        }
        case 403:
          setServerError({
            code: 403,
            message: "You don't have permission to save this recipe.",
          });
          return;
        case 404:
          setServerError({ code: 404, message: "Recipe not found." });
          return;
        case 422: {
          const details = (err as Error & { details?: unknown }).details as
            | { issues?: Array<{ path: Array<string | number>; message: string }> }
            | undefined;
          const issues = details?.issues ?? [];
          if (issues.length === 0) {
            toast.error("Couldn't save recipe — the server rejected one or more fields.");
          }
          for (const issue of issues) {
            // RHF setError on a string-typed form accepts dot-notation paths.
            const path = issue.path.join(".");
            form.setError(path, {
              type: "server",
              message: issue.message,
            });
          }
          return;
        }
        default:
          // 5xx + everything else — generic toast (no raw message exposed).
          toast.error("Couldn't save recipe — the server returned an error.");
      }
    },
  });

  // -------------------------------------------------------------------------
  // Phase 10 Plan 12 (Surface 3) — Discard-draft mutation. Fires DELETE // sanitize-allow: [A-Z]{3,5}-[0-9]{2}
  // /api/recipes/:id (the UI-SPEC §"Surface 3" line 780 discard contract) and
  // on success navigates to /recipes. The destructive confirm Dialog lives in
  // ExtractionDraftBanner; this mutation runs only after the user confirms.
  //
  // NOTE (Plan 10-08 / Phase 5 handoff): a thin DELETE /api/recipes/:id route
  // is not yet wired server-side (Phase 5 ACL has the soft-delete column from
  // Plan 10-02; the review surface uses POST /review/action with {reject}).
  // The client targets the UI-SPEC DELETE contract; the server route is a
  // follow-up. Tests mock fetch so the client flow is exercised end-to-end.
  // -------------------------------------------------------------------------
  const discardDraftMutation = useMutation<void, Error, void>({
    mutationFn: async () => {
      if (typeof recipeId !== "string" || recipeId.length === 0) {
        return;
      }
      const res = await fetch(`/api/recipes/${recipeId}`, { method: "DELETE" });
      if (!res.ok) {
        const err: Error & { status?: number } = new Error(`Discard failed: ${res.status}`);
        err.status = res.status;
        throw err;
      }
    },
    onSuccess: () => {
      navigate("/recipes");
    },
    onError: () => {
      toast.error("Couldn't discard the draft — check your connection and try again.");
    },
  });

  const handleDiscardDraft = (): void => {
    discardDraftMutation.mutate();
  };

  // Return to review (Surface 3, unreviewed path) — navigate to the review
  // page. When the form is dirty, the Phase 8 UnsavedChangesGuard (mounted by
  // EditorShell) intercepts the navigation with its own confirm Dialog.
  const handleReturnToReview = (): void => {
    if (typeof recipeId === "string" && recipeId.length > 0) {
      navigate(`/recipes/${recipeId}/review`);
    }
  };

  // -------------------------------------------------------------------------
  // Save / Discard / Load-fixture / Paste-JSON handlers.
  // -------------------------------------------------------------------------
  const onSave = (): void => {
    // form.handleSubmit's onValid callback receives the resolved (Zod-parsed)
    // values; the resolver returns `result.data` which IS RecipeDefinitionV1
    // by construction (safeParse success branch). The cast acknowledges the
    // FieldValues-typed wrapper without leaking unsafety to the mutation.
    void form.handleSubmit((values) =>
      saveMutation.mutateAsync(values as unknown as RecipeDefinitionV1),
    )();
  };

  const onDiscard = (): void => {
    form.reset(initialDefaults as unknown as FieldValues);
    setServerError(null);
  };

  const onLoadFixture = (id: FixtureId): void => {
    const raw = FIXTURE_BY_ID[id];
    // Defense-in-depth safeParse on fixture JSON per UI-SPEC line 868.
    const validation = RecipeDefinitionV1Schema.safeParse(raw);
    if (!validation.success) {
      toast.error(`Couldn't load the ${id} fixture — it does not validate.`);
      return;
    }
    if (form.formState.isDirty) {
      setPendingFixture(validation.data);
      return;
    }
    form.reset(validation.data as unknown as FieldValues);
  };

  const onPasteJson = (): void => {
    setPasteOpen(true);
  };

  // -------------------------------------------------------------------------
  // Loading copy while server fetch is pending (UI-SPEC line 398).
  // -------------------------------------------------------------------------
  if (enableQuery && recipeQuery.isLoading) {
    return (
      <main aria-label="Recipe editor" className="flex h-screen items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          <span>Loading recipe…</span>
        </div>
      </main>
    );
  }

  if (enableQuery && recipeQuery.isError) {
    return (
      <main aria-label="Recipe editor" className="mx-auto max-w-md p-8">
        <Alert variant="destructive">
          <AlertTitle>Couldn't load the recipe</AlertTitle>
          <AlertDescription>
            {recipeQuery.error instanceof Error ? recipeQuery.error.message : "Unknown error"}
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  // EditorShell renders the page-level <main aria-label="Recipe editor">; we
  // wrap in a <div> here so the page tree contains exactly ONE main landmark.
  // The SpreadVocabularyProvider wraps the tree with empty overrides so
  // useTerm() inside the constraint sub-forms (cardinality-config, etc.) falls
  // back to VOCABULARY_DEFAULTS — the author UI has no spread context.
  return (
    <div className="flex h-screen flex-col">
      <SpreadVocabularyProvider value={{}}>
        <TooltipProvider>
          <AISuggestionProvider value={aiSuggestionValue}>
            <FormProvider {...form}>
              {serverError !== null ? (
                <Alert variant="destructive">
                  <AlertTitle>
                    {serverError.code === 403 ? "Permission denied" : "Recipe not found"}
                  </AlertTitle>
                  <AlertDescription>{serverError.message}</AlertDescription>
                </Alert>
              ) : null}
              <EditorShell
                toolbar={
                  <>
                    {/* Phase 10 Plan 12 (Surface 3) — draft banner mounts ABOVE the */}
                    {/* EditorToolbar per 10-UI-SPEC component tree lines 193-194.   */}
                    <ExtractionDraftBanner
                      flags={flags}
                      onDiscard={handleDiscardDraft}
                      onReturnToReview={handleReturnToReview}
                    />
                    <EditorToolbarWrapper
                      onSave={onSave}
                      onDiscard={onDiscard}
                      onLoadFixture={onLoadFixture}
                      onPasteJson={onPasteJson}
                      isSaving={saveMutation.isPending}
                    />
                  </>
                }
              >
                <SplitPane
                  storageKey="gsd:author:split-ratio" // sanitize-allow: gsd
                  defaultRatio={0.5}
                  minRatio={0.2}
                  maxRatio={0.8}
                  ariaControls="preview-pane"
                  leftChildren={<RecipeFormPanel />}
                  rightChildren={<PreviewPane />}
                />
              </EditorShell>
              <PasteJsonDialog
                open={pasteOpen}
                onOpenChange={setPasteOpen}
                onAccept={(parsed) => form.reset(parsed as unknown as FieldValues)}
              />
              <ReplaceConfirmDialog
                open={pendingFixture !== null}
                onOpenChange={(next) => {
                  if (!next) setPendingFixture(null);
                }}
                onConfirm={() => {
                  if (pendingFixture !== null) {
                    form.reset(pendingFixture as unknown as FieldValues);
                  }
                  setPendingFixture(null);
                }}
              />
            </FormProvider>
          </AISuggestionProvider>
        </TooltipProvider>
      </SpreadVocabularyProvider>
    </div>
  );
}

// Re-export the typed form helper so other plans (Plan 12 verification +
// Plan 11 server tests) can share the form-state surface.
export type RecipeEditorForm = UseFormReturn<FieldValues>;
